import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import { SettingsResponse, Section, UserEntry } from './useGetSettings';
import { Role } from './useValidateSession';
import { UserPermission } from './useEditDefaultPermissions';
import type { EnabledNotificationTypesDict } from './useGetProfile';

export interface EditUserInput {
    userId: string;
    admin?: boolean;
    active?: boolean;
    locationId?: Array<string> | 'all';
    position?: Role;
    permissions?: UserPermission | 'admin' | 'default';
    enabledNotificationTypes?: EnabledNotificationTypesDict;
    notificationsEnabled?: boolean;
}

const editUser = async (editUserInput: EditUserInput, sessionId: string | null) => {
    return axios.patch(`${API_URL}/settings/user/edit`,
        editUserInput,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
};

export const useEditUser = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (editUserInput: EditUserInput) => editUser(editUserInput, sessionContext?.sessionId ?? null),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['/users'] });
            if (variables.userId === sessionContext?.userId) {
                queryClient.invalidateQueries({ queryKey: ['/account/validate', sessionContext?.sessionId ?? null] });
                if (
                    variables.enabledNotificationTypes !== undefined ||
                    variables.notificationsEnabled !== undefined
                ) {
                    queryClient.invalidateQueries({ queryKey: ['profile'] });
                    queryClient.invalidateQueries({ queryKey: ['/notifications'] });
                }
            }
            if (variables.position !== undefined) {
                queryClient.invalidateQueries({ queryKey: ['reporting', 'productivity'] });
            }

            queryClient.setQueryData(['settings'], (oldData: SettingsResponse | undefined) => {
                if (!oldData) return oldData;

                const newData: SettingsResponse = JSON.parse(JSON.stringify(oldData));

                const userSection = newData.topSection?.sections.find((s: Section) => s.key === 'user') as { data: UserEntry[]; } | undefined;
                if (userSection && userSection.data) {
                    const userIndex = userSection.data.findIndex((u: UserEntry) => u._id === variables.userId);
                    if (userIndex !== -1) {
                        const updatedUser = { ...userSection.data[userIndex] };

                        if (variables.admin !== undefined) updatedUser.admin = variables.admin;
                        if (variables.active !== undefined) {
                            updatedUser.active = variables.active;
                            updatedUser.status = variables.active ? 'active' : 'inactive';
                        }
                        if (variables.locationId !== undefined) updatedUser.locationId = variables.locationId;
                        if (variables.position !== undefined) updatedUser.position = variables.position;
                        if (variables.permissions !== undefined) updatedUser.permissions = variables.permissions;
                        if (variables.enabledNotificationTypes !== undefined) {
                            updatedUser.enabledNotificationTypes = variables.enabledNotificationTypes;
                        }
                        if (variables.notificationsEnabled !== undefined) {
                            updatedUser.notificationsEnabled = variables.notificationsEnabled;
                        }

                        userSection.data[userIndex] = updatedUser;
                    }
                }

                return newData;
            });
        }
    });
};

